/**
 * FirstPersonArm — a Minecraft-style first-person view model.
 *
 * A low-poly arm holding the actual SurfaceNet mesh of the current build (the same
 * model the thumbnails use, with the real terrain materials) while build mode is on,
 * and an empty hand otherwise. A punch swing fires on place/dig.
 *
 * Rendering: the arm lives in the MAIN scene (so the world's day/night lights
 * illuminate it) but on dedicated first-person layers, excluded from the normal
 * composed render. It is drawn by its own ORTHOGRAPHIC camera (`vmCamera`) so the
 * main camera's FOV/aspect never skews it and it renders at a consistent size —
 * like the build thumbnails (which also use an ortho camera). `renderFirstPersonArm`
 * draws the arm then the item, each over a cleared depth buffer, so nothing occludes
 * them.
 *
 * Module singleton, mirroring the camera/scene singletons.
 */

import * as THREE from 'three';
import type { BuildPart, Quat } from '@worldify/shared';
import { createBuildItemMeshes } from '../../ui/PresetThumbnailRenderer';
import { FIRST_PERSON_LAYER, FIRST_PERSON_ITEM_LAYER } from './firstPersonLayer';
import { getCamera } from './camera';

let vmCamera: THREE.OrthographicCamera | null = null; // dedicated view-model camera
let group: THREE.Group | null = null;   // arm rig; positioned in vmCamera space each frame
let hand: THREE.Group | null = null;    // holds the arm mesh + current build item
let armSkinMat: THREE.MeshStandardMaterial | null = null;
let heldItem: THREE.Object3D | null = null;
let heldKey = '';                        // rebuild guard (config + rotation + texture variant)

const ARM_DEPTH = 2;     // vm-camera-local Z of the arm (ortho: clip only, not size)
const CORNER_X = 0.9;    // fraction of the ortho half-width → toward the right edge
const CORNER_Y = 0.9;    // fraction of the ortho half-height → toward the bottom edge
/**
 * Overall view-model size. The ortho frustum is scaled by sqrt(w*h) (rotation-
 * invariant), so the world→pixel scale — and thus the arm's pixel size — is the
 * same in portrait and landscape. Larger VM_SIZE → smaller arm.
 */
const VM_SIZE = 2.2;
/** Scales the (camera) head-bob applied inversely to the arm — keep the arm sway subtle. */
const ARM_BOB_SCALE = 0.6;

/**
 * Orientation that makes the held item present the SAME face as its thumbnail.
 * The thumbnail camera views from (-0.8, 0.6, -0.8); applying the inverse of that
 * view orientation to the item makes the view-model camera (looking down -Z)
 * reproduce the thumbnail angle.
 */
const HELD_ITEM_QUAT = new THREE.Quaternion().setFromRotationMatrix(
  new THREE.Matrix4().lookAt(
    new THREE.Vector3(-0.8, 0.6, -0.8),
    new THREE.Vector3(0, 0, 0),
    new THREE.Vector3(0, 1, 0),
  ),
).invert();

let swing = 0; // 1 on trigger, decays to 0
let swingKind: 'punch' | 'build' | 'swap' = 'punch';
let visible = false;
let wasVisible = false;   // rising-edge detector for the reveal slide-in
let reveal = 0;           // 1 → slide up from below; decays to 0
let exitT = -1;           // -1 = not exiting; 0→1 slide-down progress on returning to explore

/** Put an object and all descendants on a single layer. */
function setLayerDeep(obj: THREE.Object3D, layer: number): void {
  obj.traverse((o) => o.layers.set(layer));
}

/** The arm's skin — exported so the shader warm-up compiles the same program (render/ShaderWarmup.ts). */
export function createArmSkinMaterial(): THREE.MeshStandardMaterial {
  return new THREE.MeshStandardMaterial({
    color: 0xd9a066, roughness: 0.85, metalness: 0.0,
    // Slight self-illumination so the hand keeps some form at night.
    emissive: 0x3a2717, emissiveIntensity: 0.35,
  });
}

/** Build the arm + its dedicated ortho camera and add them to the scene (hidden). */
export function initFirstPersonArm(scene: THREE.Scene): void {
  if (group) return;

  vmCamera = new THREE.OrthographicCamera(-1, 1, 1, -1, 0.01, 100);
  scene.add(vmCamera); // in the scene graph so its child arm renders + is lit

  group = new THREE.Group();
  group.frustumCulled = false;
  group.visible = false;

  hand = new THREE.Group();
  group.add(hand);

  armSkinMat = createArmSkinMaterial();
  // Forearm — a slim capsule (Minecraft-style) coming from the bottom-right corner,
  // the wrist behind/below the held block (which draws over it). Tuned in an
  // isolated harness to match Minecraft's block-in-hand layout.
  const arm = new THREE.Mesh(new THREE.CapsuleGeometry(0.19, 0.72, 4, 10), armSkinMat);
  arm.rotation.set(-0.1, 0.1, 0.66);
  arm.position.set(0.05, -0.06, 0.1);
  arm.frustumCulled = false;
  hand.add(arm);

  setLayerDeep(group, FIRST_PERSON_LAYER);
  vmCamera.add(group);
}

/** Trigger a one-shot arm swing. 'punch' throws forward; 'build' dips the hand down. */
export function triggerArmSwing(kind: 'punch' | 'build' = 'punch'): void {
  swing = 1;
  swingKind = kind;
}

/** Hide the arm (non-Playing modes / menu open). */
export function setFirstPersonArmVisible(v: boolean): void {
  visible = v;
  if (group) group.visible = v;
}

/** Begin the arm's slide-down exit (call when returning play→explore). No-op if already hidden. */
export function startFirstPersonArmExit(): void {
  if (visible && exitT < 0) exitT = 0;
}

/**
 * Drive the arm outside Playing mode: if an exit slide is in progress, ease it down and hide when
 * done; otherwise ensure the arm is hidden. Called every non-Playing frame from the game loop.
 */
export function tickFirstPersonArmExit(dtMs: number): void {
  if (!group) return;
  if (exitT < 0) {
    if (visible) { visible = false; group.visible = false; }
    return;
  }
  const dt = Math.min(dtMs, 100) / 1000;
  exitT = Math.min(1, exitT + dt * 2.6);
  const { halfW, halfH } = sizeVmCamera();
  const baseX = halfW * CORNER_X;
  const baseY = -halfH * CORNER_Y;
  group.visible = true;
  group.position.set(baseX, baseY - exitT * exitT * (halfH * 2), -ARM_DEPTH);
  group.rotation.set(0, 0, 0);
  if (exitT >= 1) { exitT = -1; visible = false; group.visible = false; wasVisible = false; }
}

function disposeArmObject(obj: THREE.Object3D): void {
  obj.traverse((o) => {
    if (o instanceof THREE.Mesh || o instanceof THREE.LineSegments) {
      o.geometry.dispose();
      // Only dispose dedicated held-item materials; shared terrain/water/wireframe
      // materials are reused elsewhere and must not be disposed.
      const mats = Array.isArray(o.material) ? o.material : [o.material];
      for (const m of mats) if (m && m.userData?.heldItem) m.dispose();
    }
  });
}

function clearHeldItem(): void {
  if (heldItem && hand) {
    hand.remove(heldItem);
    disposeArmObject(heldItem);
  }
  heldItem = null;
  heldKey = '';
}

function itemKey(parts: BuildPart[], rotation: Quat | undefined, variant: string): string {
  const r = rotation ? `${rotation.x.toFixed(3)},${rotation.y.toFixed(3)},${rotation.z.toFixed(3)},${rotation.w.toFixed(3)}` : '';
  const p = parts.map((pt) => `${pt.config.shape},${pt.config.mode},${pt.config.material},${pt.config.size.x},${pt.config.size.y},${pt.config.size.z},${pt.config.thickness ?? 0},${pt.config.closed ?? 1},${pt.config.arcSweep ?? 0}@${pt.offset.x},${pt.offset.y},${pt.offset.z}`).join(';');
  return `${variant}|${r}|${p}`;
}

/**
 * Size the ortho frustum so the world→pixel scale is sqrt(w*h)-based, i.e. the
 * SAME in portrait and landscape → the arm renders at a consistent pixel size.
 * Returns the current half-width/half-height for corner placement.
 */
function sizeVmCamera(): { halfW: number; halfH: number } {
  const w = Math.max(1, window.innerWidth);
  const h = Math.max(1, window.innerHeight);
  const ref = Math.sqrt(w * h);
  const halfW = 0.5 * (w / ref) * VM_SIZE;
  const halfH = 0.5 * (h / ref) * VM_SIZE;
  if (vmCamera) {
    vmCamera.left = -halfW; vmCamera.right = halfW;
    vmCamera.top = halfH; vmCamera.bottom = -halfH;
    vmCamera.updateProjectionMatrix();
  }
  return { halfW, halfH };
}

/**
 * Per-frame update: anchors the arm to the bottom-right of the ortho frustum (so it
 * sits in the corner at a consistent size in any orientation), applies the punch
 * swing + inverse head-bob, and rebuilds the held build mesh only when it changes.
 */
export function updateFirstPersonArm(opts: {
  visible: boolean;
  buildMode: boolean;
  rotation?: Quat;
  parts?: BuildPart[];
  texturesReady: boolean;
  variant: string;
  headBob: number;
  dtMs: number;
}): void {
  if (!group || !hand) return;

  // Rising edge of visibility → slide the arm up from below (played once the camera intro ends).
  if (opts.visible && !wasVisible) { reveal = 1; exitT = -1; }
  wasVisible = opts.visible;

  visible = opts.visible;
  group.visible = opts.visible;
  if (!opts.visible) return;

  const dt = Math.min(opts.dtMs, 100) / 1000;

  const { halfW, halfH } = sizeVmCamera();
  const baseX = halfW * CORNER_X;
  const baseY = -halfH * CORNER_Y;

  // Swing (decays 1 → 0; peaks mid-decay for an out-and-back motion).
  if (swing > 0) swing = Math.max(0, swing - dt * 4.5);
  const punch = Math.sin(swing * Math.PI);

  // Reveal slide-in (decays 1 → 0); offsets the arm below the frustum, easing up into view.
  if (reveal > 0) reveal = Math.max(0, reveal - dt * 2.2);
  const revealDrop = reveal * reveal * (halfH * 2); // ease-in; halfH*2 is fully off the bottom

  // Animate by TRANSLATION only — no group rotation, which previously pitched the whole subtree
  // and skewed the held item. Inverse head-bob keeps the arm counter-moving to the walk bob.
  const bobY = baseY - opts.headBob * ARM_BOB_SCALE - revealDrop;
  if (swingKind === 'build' || swingKind === 'swap') {
    // Placing / swapping items: dip the hand downward.
    group.position.set(baseX, bobY - punch * 0.22, -ARM_DEPTH);
  } else {
    // Punching: a quick jab up-and-toward-centre. (The view-model camera is orthographic, so a
    // -Z "forward" thrust would be invisible — animate in X/Y instead.)
    group.position.set(baseX - punch * 0.3, bobY + punch * 0.28, -ARM_DEPTH);
  }
  group.rotation.set(0, 0, 0);

  // Held item — the real build mesh for the selected slot, shown whenever an item is selected
  // (walking or building). Empty slots have zero-size parts → createBuildItemMeshes yields nothing,
  // so the hand is empty.
  if (opts.parts && opts.parts.length && opts.texturesReady) {
    const key = itemKey(opts.parts, opts.rotation, opts.variant);
    if (key !== heldKey) {
      if (heldItem && reveal <= 0) {
        // Switching item while one is held: bob the arm down and swap the mesh at the bottom of
        // the dip (old mesh on the way down, new on the way up).
        if (swingKind !== 'swap' || swing <= 0) { swing = 1; swingKind = 'swap'; }
        if (swing <= 0.5) { rebuildHeldItem(opts); heldKey = key; }
      } else {
        // First reveal / no current item: swap in place.
        rebuildHeldItem(opts);
        heldKey = key;
      }
    }
  } else if (heldItem) {
    clearHeldItem();
    heldKey = '';
  }
}

/** Rebuild the held-item mesh from the current opts and mount it in the hand at its resting pose. */
function rebuildHeldItem(opts: { parts?: BuildPart[]; rotation?: Quat }): void {
  if (!hand) return;
  clearHeldItem();
  if (!opts.parts || !opts.parts.length) return;
  const mesh = createBuildItemMeshes(opts.parts, opts.rotation);
  if (mesh) {
    mesh.position.set(-0.16, 0.24, -0.3);  // held in the lower-right, Minecraft-style
    mesh.quaternion.copy(HELD_ITEM_QUAT);  // thumbnail's 3/4 view angle
    mesh.scale.multiplyScalar(1.27);       // ~0.33 world extent (from the 0.26 base)
    setLayerDeep(mesh, FIRST_PERSON_ITEM_LAYER); // drawn over the arm
    hand.add(mesh);
    heldItem = mesh;
  }
}

/**
 * Render the arm on top of the composited frame, with its own ortho camera. Draws
 * the arm layer then the item layer, each over a cleared depth buffer, using the
 * same renderer/tone-mapping as the world. Call right after the post pipeline's composite.
 */
export function renderFirstPersonArm(renderer: THREE.WebGLRenderer, scene: THREE.Scene): void {
  if (!group || !vmCamera || !visible) return;

  // Match the main camera's orientation so the scene lights hit the arm/item the
  // same way they'd hit the world in view — i.e. lighting responds to the player's
  // facing + time of day. The arm stays screen-locked (it's a child at a fixed
  // local offset) and undistorted (ortho); only the light direction relative to it
  // changes. The object-space held-item texture is unaffected by this rotation.
  const mainCam = getCamera();
  if (mainCam) vmCamera.quaternion.copy(mainCam.quaternion);

  const prevShadowAuto = renderer.shadowMap.autoUpdate;
  const prevAutoClear = renderer.autoClear;
  // A Color background makes three CLEAR THE COLOUR BUFFER even with autoClear off (WebGLBackground:
  // `forceClear`), so these overlay passes would wipe the composed frame and leave the arm on a flat
  // background — which is exactly what the LOD debug view's backdrop did in first person. An overlay
  // draws no background at all; the world pass owns it.
  const prevBackground = scene.background;
  renderer.shadowMap.autoUpdate = false;   // don't recompute world shadows for this pass
  renderer.autoClear = false;
  scene.background = null;

  // Pass 1 — the arm, over the composited world.
  renderer.clearDepth();
  vmCamera.layers.set(FIRST_PERSON_LAYER);
  renderer.render(scene, vmCamera);

  // Pass 2 — the held item, over the arm (fresh depth → always on top, no z-fight).
  renderer.clearDepth();
  vmCamera.layers.set(FIRST_PERSON_ITEM_LAYER);
  renderer.render(scene, vmCamera);

  // Restore renderer state exactly as it was: a caller that had autoClear off keeps it off.
  renderer.autoClear = prevAutoClear;
  renderer.shadowMap.autoUpdate = prevShadowAuto;
  scene.background = prevBackground;
}

/** Dispose the arm resources. */
export function disposeFirstPersonArm(): void {
  if (!group) return;
  clearHeldItem();
  vmCamera?.parent?.remove(vmCamera);
  group.parent?.remove(group);
  disposeArmObject(group);
  armSkinMat?.dispose();
  armSkinMat = null;
  group = null;
  hand = null;
  vmCamera = null;
}
